import { IconButton } from '@mui/material'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import React from 'react'
import { useDispatch } from 'react-redux'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import { useTranslate } from 'src/localization/Trns'
import {
  APIKeyResponse,
  formatDateString,
} from 'src/queries/organizations/apikeys.queries'
import { createSetIsDeleteAPIKeyPopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

type APIKeysDataGridProps = {
  apiKeys: APIKeyResponse[]
  defaultColumnVisibilityModel: GridColumnVisibilityModel
  clientHeight?: number
}

const renderExpiresCell = (
  params: GridRenderCellParams<APIKeyResponse>,
): React.ReactElement => {
  const apiKey = params.row
  return (
    <DataGridCell text={formatDateString(apiKey.expires)} params={params} />
  )
}
const renderCreatedAtCell = (
  params: GridRenderCellParams<APIKeyResponse>,
): React.ReactElement => {
  const apiKey = params.row
  return (
    <DataGridCell text={formatDateString(apiKey.createdAt)} params={params} />
  )
}

const padStringWithAsterisk = (input: string, targetLength: number): string => {
  if (input.length >= targetLength) {
    return input
  }

  return '*'.repeat(targetLength - input.length) + input
}

const renderSecretKeyCell = (
  params: GridRenderCellParams<APIKeyResponse>,
): React.ReactElement => {
  const apiKey = params.row
  return (
    <DataGridCell
      text={
        <span
          css={css`
            font-family: monospace;
          `}
        >
          {padStringWithAsterisk(apiKey.secretId, 22)}
        </span>
      }
      params={params}
    />
  )
}

const renderIdCell = (
  params: GridRenderCellParams<APIKeyResponse>,
): React.ReactElement => {
  const apiKey = params.row
  return (
    <DataGridCell
      text={
        <span
          css={css`
            font-family: monospace;
          `}
        >
          {apiKey.id}
        </span>
      }
      params={params}
    />
  )
}

const APIKeyDataGrid: React.FC<APIKeysDataGridProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()

  const columns: Array<GridColDef<APIKeyResponse>> = [
    {
      field: 'id',
      headerName: translate('API Key ID'),
      flex: 1,
      minWidth: 150,
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      renderCell: renderIdCell,
    },
    {
      field: 'secretId',
      headerName: translate('API Secret Key'),
      flex: 1,
      minWidth: 150,
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      renderCell: renderSecretKeyCell,
    },
    {
      field: 'expires',
      headerName: translate('Expiration Date (MM/DD/YY)'),
      flex: 1,
      minWidth: 150,
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      renderCell: renderExpiresCell,
    },
    {
      field: 'createdAt',
      headerName: translate('Created Date (MM/DD/YY)'),
      flex: 1,
      minWidth: 150,
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      renderCell: renderCreatedAtCell,
    },
    {
      field: 'actions',
      headerName: translate('Delete'),
      disableColumnMenu: true,
      resizable: false,
      sortable: false,
      hideable: false,
      headerClassName: 'hideRightSeparator',
      renderCell: (item): React.ReactElement => {
        const apiKey = item.row
        return (
          <IconButton
            onClick={(): void => {
              dispatch(
                createSetIsDeleteAPIKeyPopupOpen(
                  true,
                  apiKey.id,
                  formatDateString(apiKey.expires),
                ),
              )
            }}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                width: 100%;
                height: 100%;
              `}
            >
              <SvgTrash
                css={css`
                  transform: scale(0.9);
                `}
              />
            </span>
          </IconButton>
        )
      },
      renderHeader: () => <></>,
    },
  ]
  return (
    <DataGrid
      height={props.clientHeight}
      rows={props.apiKeys}
      columns={columns}
      filtersBody={{}}
      rowHeight={DataGridRowHeight.Default}
      initialState={{
        pinnedColumns: {
          right: ['delete'],
        },
      }}
      disableColumnFilter
    />
  )
}

export default APIKeyDataGrid
